import { Nullable, SelectOption } from 'global';
import { v4 as uuidv4 } from 'uuid';
import { CustomerVisibility } from 'modules/dealSheet';
import { QuoteFeeType } from '@trnsact/trnsact-shared-types';
import { Charge, ChargeAttachType, ChargeBucketRef, ChargeParentKind } from 'modules/equipment/types';

import { chargeTypesByLabels, generateEquipmentName, newChargeRow } from '../../equipment';
import { InvoiceListingBucket, Quote, QuoteChargeListingEntity, QuoteLineItemType } from '../types';

interface BuildParentOptionsParams {
  currentRowIndex: number;
  scope: ChargeBucketRef;
  rows: QuoteChargeListingEntity[];
}

export interface ChargeParentOption extends SelectOption {
  kind: ChargeParentKind;
}

export class ChargeListingService {
  /**
   * DR-10718: a line shown in the "Internal / Dealer cost" group. The server says, per quote, whether internal-only
   * fees are a dealer cost (its fees-and-taxes version); a quote not created yet will follow the current rules.
   */
  public isDealerCostRow(row: QuoteChargeListingEntity, quote: Nullable<Quote>): boolean {
    const internalFeesAreDealerCost = quote ? !!quote.internalFeesAreDealerCost : true;

    return (
      internalFeesAreDealerCost &&
      row.type === QuoteLineItemType.Invoice &&
      row.charge?.customerVisibility === CustomerVisibility.InternalOnly
    );
  }

  public resolveParentScope(row: QuoteChargeListingEntity, invoiceId: string) {
    if (row.type === QuoteLineItemType.Equipment) {
      const equipmentId = row.equipment?.equipmentId;

      return equipmentId ? { id: equipmentId, type: ChargeAttachType.Equipment } : null;
    }

    const bucket = row.charge?.groupBucket;

    if (!bucket) return null;

    if (bucket.type === ChargeAttachType.Invoice) {
      return { id: bucket.id || invoiceId, type: ChargeAttachType.Invoice };
    }

    return bucket;
  }

  /**
   * Groups the rows for display. The form keeps its rows in this same order (see quoteMapper.toListingRows and
   * getInsertIndex), because the listing derives each row's form index from its position across the groups.
   */
  public buildBuckets(
    rows: QuoteChargeListingEntity[],
    invoiceId: string,
    quote: Nullable<Quote>
  ): InvoiceListingBucket[] {
    const equipmentBuckets: InvoiceListingBucket[] = [];

    const invoiceBucket: InvoiceListingBucket = {
      data: [],
      id: invoiceId,
      title: 'Invoice Level',
      type: ChargeAttachType.Invoice,
    };

    // DR-10718: internal-only fees are listed last, outside the customer's total
    const dealerCostBucket: InvoiceListingBucket = {
      data: [],
      id: `${invoiceId}:dealer-cost`,
      title: 'Internal / Dealer cost',
      type: ChargeAttachType.Invoice,
      isDealerCost: true,
    };

    let currentBucket: InvoiceListingBucket | null = null;

    rows.forEach(row => {
      if (this.isDealerCostRow(row, quote)) {
        dealerCostBucket.data.push(row);
        return;
      }

      if (row.type === QuoteLineItemType.Equipment) {
        const equipmentId = row.equipment?.equipmentId;

        if (!equipmentId) {
          invoiceBucket.data.push(row);
          currentBucket = null;
          return;
        }

        currentBucket = {
          data: [row],
          id: equipmentId,
          type: ChargeAttachType.Equipment,
          title: row.equipment ? generateEquipmentName(row.equipment) : 'Equipment',
        };

        equipmentBuckets.push(currentBucket);
        return;
      }

      const attachId = this.getEquipmentBucketId(row);

      if (attachId) {
        if (currentBucket && attachId === currentBucket.id) {
          currentBucket.data.push(row);
          return;
        }

        const targetBucket = equipmentBuckets.find(bucket => bucket.id === attachId);

        if (targetBucket) {
          targetBucket.data.push(row);
          return;
        }
      }

      invoiceBucket.data.push(row);
    });

    return [...equipmentBuckets, invoiceBucket, ...(dealerCostBucket.data.length ? [dealerCostBucket] : [])];
  }

  /** Where a new row of `bucket` goes so the form order keeps matching buildBuckets (dealer-cost rows stay last). */
  public getInsertIndex(rows: QuoteChargeListingEntity[], bucket: ChargeBucketRef, quote: Nullable<Quote>): number {
    const firstDealerCostIdx = rows.findIndex(row => this.isDealerCostRow(row, quote));
    const endOfCustomerRows = firstDealerCostIdx === -1 ? rows.length : firstDealerCostIdx;

    if (bucket.type !== ChargeAttachType.Equipment) return endOfCustomerRows;

    const targetId = bucket.id;
    let equipmentIdx = -1;
    let lastAttachedIdx = -1;

    rows.forEach((row, index) => {
      if (this.isDealerCostRow(row, quote)) return;

      if (row.type === QuoteLineItemType.Equipment && row.equipment?.equipmentId === targetId) {
        equipmentIdx = index;
        lastAttachedIdx = index;
        return;
      }

      if (equipmentIdx !== -1 && this.getEquipmentBucketId(row) === targetId) {
        lastAttachedIdx = index;
      }
    });

    return lastAttachedIdx === -1 ? endOfCustomerRows : lastAttachedIdx + 1;
  }

  public buildParentOptions({ rows, currentRowIndex, scope }: BuildParentOptionsParams): ChargeParentOption[] {
    const options: ChargeParentOption[] = [];

    if (scope.type === ChargeAttachType.Equipment) {
      const equipmentRow = rows.find(
        row => row.type === QuoteLineItemType.Equipment && row.equipment?.equipmentId === scope.id
      );

      if (equipmentRow?.equipment) {
        options.push({
          kind: 'equipment',
          value: equipmentRow.equipment.equipmentId,
          label: generateEquipmentName(equipmentRow.equipment),
        });
      }
    }

    rows.forEach((row, index) => {
      if (index === currentRowIndex) return;
      if (row.type !== QuoteLineItemType.Invoice) return;

      const charge = row.charge;
      if (!charge?.id || !charge?.label || !charge?.groupBucket) return;
      if (charge.customerVisibility === CustomerVisibility.InternalOnly) return;
      if (!this.isChargeInScope(charge, scope)) return;

      options.push({ kind: 'charge', value: charge.id, label: charge.label });
    });

    return options;
  }

  public createCustomChargeRow(bucket: ChargeBucketRef, name: string): QuoteChargeListingEntity {
    return {
      equipment: null,
      type: QuoteLineItemType.Invoice,
      charge: {
        id: uuidv4(),
        label: name,
        parentId: null,
        price: newChargeRow,
        groupBucket: bucket,
        financeType: 'other',
        type: QuoteFeeType.Other,
        customerVisibility: CustomerVisibility.Visible,
      },
    };
  }

  public createChargeRowFromTemplate(
    bucket: ChargeBucketRef,
    template: Omit<Charge, 'groupBucket'>
  ): QuoteChargeListingEntity {
    return {
      equipment: null,
      type: QuoteLineItemType.Invoice,
      charge: {
        ...template,
        id: uuidv4(),
        parentId: null,
        groupBucket: bucket,
        customerVisibility: CustomerVisibility.Visible,
        financeType: chargeTypesByLabels[template.label],
      },
    };
  }

  private getEquipmentBucketId(row: QuoteChargeListingEntity) {
    const bucket = row.charge?.groupBucket;

    if (!bucket || bucket.type !== ChargeAttachType.Equipment) return null;

    return bucket.id ?? null;
  }

  private isChargeInScope(charge: Charge, scope: ChargeBucketRef): boolean {
    const { groupBucket } = charge;

    return groupBucket.type === scope.type && groupBucket.id === scope.id;
  }
}
